import {
    CreateScenarioRequest, FeatureManagementType, MetadataEntityType, Model, NonModelledElementsStatus, ProblemDetails, RebarEstimationStatus, Scenario, ScenarioMetadata,
} from "@api/apiClient";
import { useAppSettings } from "@api/appSettings";
import { useProject } from "@api/projects";
import { useCreateScenario, useScenarios } from "@api/scenarios";
import MicroChip from "@components/MicroChip/MicroChip";
import { useAdditionalMetadataFeature } from "@hooks/useAdditionalMetadataFeature";
import useAppStore from "@hooks/useAppStore";
import i18n from "@i18n/i18n";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
    Card, CardContent, CardHeader, Chip, FormControl,
    Grid,
    InputLabel, MenuItem, Select,
    Switch, Typography, useTheme
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import _ from "lodash";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ScenarioMetadataForm } from "./ScenarioMetadataForm";

// New Import
import { DropDownOption } from "@/types/dropdown";
import { useProfiles } from "@api/profiles";


export interface ScenariosTabProps {
  open: boolean;
  onClose: () => void;
}

const AddScenarioDialog = observer((props: ScenariosTabProps) => {
  const appStore = useAppStore();
  const theme = useTheme();
  const { id: projectId } = useParams();
  const project = useProject(projectId!);
  const scenarios = useScenarios(projectId!);
  const createScenario = useCreateScenario();
  const appSettings = useAppSettings();
  const { isEnabled: showMetadata } = useAdditionalMetadataFeature();

  const [formData, setFormData] = useState<CreateScenarioRequest>(
    new CreateScenarioRequest({
      projectId,
      name: "",
      description: "",
      rebarEstimation: false,
      nonModelledElements: false,
      projectPhaseId: "", 
      isFinalforProjectPhase: false,
      
    }),
  );

  const [metadata, setMetadata] = useState<ScenarioMetadata>(
    new ScenarioMetadata({
      totalFloorArea: project.data?.project?.totalFloorArea,
      serviceLife: project.data?.project?.serviceLife,
    }),
  );

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [duplicateScenarioError, setDuplicateScenarioError] = useState(false);
  const [modelSelectionError, setModelSelectionError] = useState(false);
  const [scenarioTypeErrorMessage, setScenarioTypeErrorMessage] = useState("");

  // === NEW: Profile State ===
  const { data: profileData } = useProfiles();
  const [selectedProfileValue, setSelectedProfileValue] = useState<string>("");
  const [profileOptions, setProfileOptions] = useState<{ label: string; value: string }[]>([]);
  const [projectPhaseOptions, setProjectPhaseOptions] = useState<DropDownOption[]>([]);
  useEffect(() => {
  // Filter for ProjectPhase entities and map to dropdown options
  const options: DropDownOption[] =
    appStore.applicationSettings?.metadataEntities
      ?.filter(entity => entity.type === MetadataEntityType.ProjectPhase)
      .map(entity => ({
        key: entity.metadataEntityId as string,
        label: entity.name as string,
      })) ?? [];
  setProjectPhaseOptions(options);
}, [appStore.applicationSettings?.metadataEntities]);

  // Scenario type options from MetadataEntities (type = ScenarioType); "None" is seeded in DB, no static duplicate
  const scenarioTypeOptions: DropDownOption[] = useMemo(() => {
    return (
      appStore.applicationSettings?.metadataEntities
        ?.filter((entity) => entity.type === MetadataEntityType.ScenarioType)
        .map((entity) => ({
          key: entity.metadataEntityId as string,
          label: entity.name as string,
        })) ?? []
    );
  }, [appStore.applicationSettings?.metadataEntities]);

  const defaultScenarioTypeId = useMemo(
    () => scenarioTypeOptions.find((o) => o.label?.toLowerCase() === "none")?.key ?? "",
    [scenarioTypeOptions]
  );

  const finalScenarioTypeId = useMemo(
    () => scenarioTypeOptions.find((o) => o.label?.toLowerCase() === "final")?.key ?? "",
    [scenarioTypeOptions]
  );

  const finalScenarioInProject = useMemo(
    () =>
      scenarios.data?.find(
        (s: Scenario) =>
          s.scenarioTypeId === finalScenarioTypeId ||
          s.scenarioTypeEntity?.name?.toLowerCase() === "final"
      ),
    [finalScenarioTypeId, scenarios.data]
  );

  const selectedScenarioTypeId = formData.scenarioTypeId ?? defaultScenarioTypeId;

  const isFinalScenarioTypeSelected =
    !!finalScenarioTypeId && selectedScenarioTypeId === finalScenarioTypeId;

  useEffect(() => {
    if (project.data) {
      const createScenarioInput = new CreateScenarioRequest({
        projectId,
        name: "",
        description: "",
        rebarEstimation:
          project.data?.project?.rebarEstimationStatus === RebarEstimationStatus.Enabled ||
          project.data?.project?.rebarEstimationStatus === RebarEstimationStatus.Estimated,
        nonModelledElements:
          project.data?.project?.nonModelledElementsStatus === NonModelledElementsStatus.Enabled ||
          project.data?.project?.nonModelledElementsStatus === NonModelledElementsStatus.Estimated,
        modelIds: appStore.project?.models?.map((model: Model) => model.modelId!) || [],
      });
      setFormData(createScenarioInput);
    }
  }, [project.data]);

  // === NEW: Populate profile dropdown ===
  useEffect(() => {
    if (!appSettings.data?.profileLevelRebarSettings) return;

    if (profileData && profileData.length > 0) {
      const _profileOptions = profileData.map((profile) => ({
        label: profile.name ?? "",
        value: profile.profileId ?? "",
      }));

      setProfileOptions([
        { label: i18n.t("scenarios-tab.scenario-dialog.profile-input.placeholder"), value: "" },
        ..._profileOptions,
      ]);

      const projectProfileId = project.data?.project?.profile?.profileId ?? "";
      const isValidProfile = _profileOptions.some((opt) => opt.value === projectProfileId);

      setSelectedProfileValue(isValidProfile ? projectProfileId : _profileOptions[0]?.value ?? "");
    } else {
      setProfileOptions([{ label: i18n.t("scenarios-tab.scenario-dialog.profile-input.placeholder"), value: "" }]);
      setSelectedProfileValue("");
    }
  }, [profileData, project.data]);

  const handleInputChange = (event: any) => {
    setFormData(new CreateScenarioRequest({ ...formData, [event.target.name]: event.target.value }));
    if (event.target.name === "name") {
      const hasMatch = scenarios.data?.find((x: Scenario) => x.name?.toLocaleLowerCase() === event.target.value.trim().toLocaleLowerCase());
      setDuplicateScenarioError(hasMatch !== undefined);
    }
  };

  const handleMetadataChange = (field: string, value: number | undefined) => {
    setMetadata((prev) => {
      const newData = {
        totalFloorArea: prev.totalFloorArea,
        serviceLife: prev.serviceLife,
        [field]: value,
      };
      return new ScenarioMetadata(newData);
    });
  };

  const handleModelInputChange = (_event: any, value: any) => {
    const modelIds = value?.map((v: Model) => v.modelId);
    setModelSelectionError(modelIds?.length === 0);
    setFormData(new CreateScenarioRequest({ ...formData, modelIds }));
  };

  const handleRebarEstimationToggle = () => {
    setFormData({ ...formData, rebarEstimation: !formData.rebarEstimation } as any);
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    const modelIds = formData.modelIds ?? [];
    if (formData.name?.trim().length !== 0 && modelIds.length > 0) {
      try {
        setIsSubmitted(true);
        setScenarioTypeErrorMessage("");
        await handleCreateScenario(formData);
      } catch (error: unknown) {
        const d = error as ProblemDetails;
        if (d && d.status === 409) {
          setScenarioTypeErrorMessage(String(d.detail));
        }
      } finally {
        setIsSubmitted(false);
      }
    }
  };

  const handleCreateScenario = async (formData: any) => {
    const createScenarioInput: any = new CreateScenarioRequest({
      ...formData,
      profileId: selectedProfileValue,
      scenarioTypeId: formData.scenarioTypeId || defaultScenarioTypeId || undefined,
    });

    if (showMetadata) {
      const scenarioMetadata = new ScenarioMetadata({
        totalFloorArea: metadata.totalFloorArea,
        serviceLife: metadata.serviceLife,
      });

      (createScenarioInput as CreateScenarioRequest).scenarioMetadata = scenarioMetadata;
    }

    await createScenario.mutateAsync(createScenarioInput);
    handleClose();
  };

  const handleClose = () => {
    setDuplicateScenarioError(false);
    props.onClose();
  };

  return (
    <Dialog fullWidth={true} maxWidth={"sm"} open={props.open} onClose={handleClose}>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {i18n.t("scenarios-tab.scenario-dialog.title")}
        <IconButton onClick={handleClose} disabled={isSubmitted}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Stack mt={1} spacing={2}>
            <TextField
              label={i18n.t("scenarios-tab.scenario-dialog.name-input.title")}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
              fullWidth
              required
              disabled={isSubmitted}
              error={(isSubmitted && _.isEmpty(formData.name?.trim())) || duplicateScenarioError}
              helperText={
                isSubmitted && _.isEmpty(formData.name?.trim())
                  ? i18n.t("scenario.helper.scenario-name")
                  : duplicateScenarioError
                    ? i18n.t("scenarios-tab.scenario-dialog.name-input.duplicate")
                    : ""
              }
            />
            <TextField
              label={i18n.t("scenarios-tab.scenario-dialog.description-input.title")}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              margin="normal"
              variant="outlined"
              fullWidth
              required
              disabled={isSubmitted}
              error={isSubmitted && _.isEmpty(formData.description?.trim())}
              helperText={isSubmitted && _.isEmpty(formData.description?.trim()) ? i18n.t("scenario.helper.scenario-description") : ""}
            />

            {/* Additional Metadata Fields */}
            {showMetadata && <ScenarioMetadataForm metadata={metadata} onChange={handleMetadataChange} />}

            {/* Profile Dropdown */}
            {appSettings.data?.profileLevelRebarSettings && (
              <FormControl fullWidth margin="normal" disabled={isSubmitted}>
                <InputLabel>{i18n.t("scenarios-tab.scenario-dialog.profile-input.title")}</InputLabel>
                <Select value={selectedProfileValue} label={i18n.t("scenarios-tab.scenario-dialog.profile-input.title")} onChange={(e) => setSelectedProfileValue(e.target.value)}>
                  {profileOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Autocomplete
              multiple
              disableCloseOnSelect
              options={appStore.project?.models || []}
              getOptionKey={(option: Model) => option.modelId!}
              getOptionLabel={(option: any) => `${option?.modelName} (${option?.modelVersion})`}
              defaultValue={appStore.project?.models || []}
              isOptionEqualToValue={(option, value) => option.modelId === value.modelId}
              renderOption={(props: any, option: Model, { selected }) => (
                <Box
                  {...props}
                  key={option.modelId}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px",
                    alignItems: "center",
                    minWidth: "200px",
                  }}
                >
                  <Checkbox checked={selected} />
                  <Box sx={{ flex: 1, display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2">{option?.modelName}</Typography>
                    <MicroChip label={option?.modelVersion || ""} />
                  </Box>
                </Box>
              )}
              onChange={handleModelInputChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={i18n.t("scenarios-tab.scenario-dialog.models-select.input.title")}
                  placeholder={i18n.t("scenarios-tab.scenario-dialog.models-select.input.placeholder")}
                  error={modelSelectionError}
                  helperText={
                    modelSelectionError
                      ? i18n.t("scenarios-tab.scenario-dialog.models-select.input.helper-text")
                      : ""
                  }
                />
              )}
              disabled={isSubmitted}
              renderTags={(value, getTagProps) => {
                return value.map((option: Model, index: number) => {
                  return (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.modelId}
                      label={
                        <>
                          {option?.modelName}
                          <MicroChip label={option?.modelVersion || ""} sx={{ marginLeft: 1, height: "20px" }} />
                        </>
                      }
                      sx={{ alignItems: "center" }}
                    />
                  );
                });
              }}
            />

            {appStore.hasFeature(FeatureManagementType.ProjectPhase) && (
              <FormControl fullWidth>
                <InputLabel>{i18n.t("scenarios-tab.scenario-dialog.project-phase-input.title")}</InputLabel>
                <Select
                  value={formData.projectPhaseId ?? ""}
                  label="Project Phase"
                  onChange={(event) => {
                    setFormData(new CreateScenarioRequest({ ...formData, projectPhaseId: event.target.value as string }));
                  }}
                >
                  {projectPhaseOptions.map((option: DropDownOption) => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Card elevation={3} sx={{ "& .MuiCardHeader-root": { backgroundColor: theme.palette.background.default } }}>
              {(appStore.hasFeature(FeatureManagementType.ProjectPhase) || (!appSettings.data?.profileLevelRebarSettings && appSettings.data?.rebarEstimation)) && (
                <CardHeader title={<Typography variant="body2">{i18n.t("scenarios-tab.title.scenario-settings")}</Typography>} elevation={3} />
              )}
              {!appSettings.data?.profileLevelRebarSettings && appSettings.data?.rebarEstimation && (
                <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body1">{i18n.t("scenarios-tab.title.rebar-estimation")}</Typography>
                    <Typography variant="subtitle1" color={theme.palette.grey[500]} fontSize="0.75rem">
                      {i18n.t("scenarios-tab.description.rebar-estimation")}
                    </Typography>
                  </Box>
                  <Switch checked={formData.rebarEstimation} onChange={handleRebarEstimationToggle} disabled={isSubmitted} />
                </CardContent>
              )}
              {appStore.hasFeature(FeatureManagementType.ProjectPhase) && (
                <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body1">{i18n.t("scenarios-tab.scenario-dialog.final-for-phase.title")}</Typography>
                    {formData.isFinalforProjectPhase &&
                      formData.projectPhaseId &&
                      (() => {
                        const existingFinalScenario = scenarios.data?.find((s: Scenario) => s.projectPhaseId === formData.projectPhaseId && s.isFinalForProjectPhase === true);
                        return existingFinalScenario ? (
                          <Typography variant="caption" color="warning.main">
                            {i18n.t("scenarios-tab.scenario-dialog.final-for-phase-disclaimer.title", { projectPhase: formData.projectPhaseId })}
                          </Typography>
                        ) : null;
                      })()}
                  </Box>
                  <Switch
                    checked={formData.isFinalforProjectPhase}
                    onChange={(e) => setFormData(new CreateScenarioRequest({ ...formData, isFinalforProjectPhase: e.target.checked }))}
                    disabled={isSubmitted}
                  />
                </CardContent>
              )}
            </Card>

            {/* Scenario Type Dropdown - Feature flagged; options from MetadataEntities (type = ScenarioType) */}
            {appStore.hasFeature(FeatureManagementType.ScenarioType) && (
              <FormControl fullWidth margin="normal" disabled={isSubmitted}>
                <InputLabel>{i18n.t("scenarios-tab.scenario-dialog.scenario-type-input.title")}</InputLabel>
                <Select
                  value={selectedScenarioTypeId}
                  label={i18n.t("scenarios-tab.scenario-dialog.scenario-type-input.title")}
                  onChange={(e) =>
                    setFormData(
                      new CreateScenarioRequest({
                        ...formData,
                        scenarioTypeId: (e.target.value as string) || undefined,
                      })
                    )
                  }
                >
                  {scenarioTypeOptions.map((opt) => (
                    <MenuItem key={opt.key} value={opt.key}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {appStore.hasFeature(FeatureManagementType.ScenarioType) && scenarioTypeErrorMessage && (
              <Typography variant="caption" color="error" component="span">
                {scenarioTypeErrorMessage}
              </Typography>
            )}

            {appStore.hasFeature(FeatureManagementType.ScenarioType) &&
              !scenarioTypeErrorMessage &&
              isFinalScenarioTypeSelected &&
              finalScenarioInProject && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <WarningAmberIcon color="warning" fontSize="small" />
                  <Typography variant="caption" color="warning.main">
                    {i18n.t("scenarios-tab.scenario-dialog.scenario-type-final-warning", {
                      name: finalScenarioInProject?.name ?? "-",
                    })}
                  </Typography>
                </Box>
              )}

            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
              <Grid container spacing={2} sx={{ justifyContent: "end" }}>
                <Grid>
                  <Button variant="outlined" onClick={handleClose} disabled={isSubmitted}>
                    {i18n.t("scenarios-tab.scenario-dialog.close-button.title")}
                  </Button>
                </Grid>
                <Grid>
                  <Button autoFocus loading={isSubmitted} disabled={duplicateScenarioError} variant="contained" type="submit">
                    {i18n.t("scenarios-tab.scenario-dialog.save-button.title")}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
});

export default AddScenarioDialog;
